import React, { useEffect, useState } from 'react';
import {
    Box, Chip, Typography, CircularProgress, Tooltip, TextField, IconButton,
    Stack,
} from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';

/**
 * Seletor de servidores DNS — chips para nós configurados, chips
 * para DNS público (vindo do backend) e input pra adicionar IPs custom.
 *
 * Props:
 *   value         { node_ids: [], public_ids: [], custom_servers: [] }
 *   onChange      (nextValue) => void
 *   disabled      bool
 *   defaultPublic bool   se true e não há valor inicial, pré-marca os 2 mais comuns
 */
const FALLBACK_PUBLIC = [
    { id: 'google',     name: 'Google',     server: '8.8.8.8' },
    { id: 'cloudflare', name: 'Cloudflare', server: '1.1.1.1' },
];

const isValidIp = (s) => {
    if (!s) return false;
    // IPv4 simples
    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(s)) {
        return s.split('.').every((p) => Number(p) >= 0 && Number(p) <= 255);
    }
    // IPv6 — heurística: ":" presente e parse em new URL falha pra IPv4
    if (s.includes(':')) {
        try {
            // Truque: URL aceita IPv6 entre colchetes
            new URL(`http://[${s}]`);
            return true;
        } catch { return false; }
    }
    return false;
};

export default function ServerPickerInline({ value, onChange, disabled = false, defaultPublic = true }) {
    const [nodes, setNodes] = useState([]);
    const [publics, setPublics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [customInput, setCustomInput] = useState('');
    const [customError, setCustomError] = useState('');

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get('/dns/node?limit=100').catch(() => ({ data: { data: [] } })),
            api.get('/dns/node/public').catch(() => ({ data: { data: FALLBACK_PUBLIC } })),
        ]).then(([nRes, pRes]) => {
            if (cancelled) return;
            const nList = nRes.data?.data || nRes.data?.results || nRes.data || [];
            const pList = pRes.data?.data || FALLBACK_PUBLIC;
            const nArr = Array.isArray(nList) ? nList : (nList.data || []);
            setNodes(nArr);
            setPublics(pList);
            // Pré-seleção: todos os nós + 2 públicos comuns se defaultPublic
            if (!value || (
                (value.node_ids?.length || 0) === 0 &&
                (value.public_ids?.length || 0) === 0 &&
                (value.custom_servers?.length || 0) === 0
            )) {
                onChange?.({
                    node_ids: nArr.map((n) => n.id),
                    public_ids: defaultPublic ? ['google', 'cloudflare'] : [],
                    custom_servers: [],
                });
            }
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, []);

    const v = value || { node_ids: [], public_ids: [], custom_servers: [] };
    const isNodeSel = (id) => v.node_ids.includes(id);
    const isPubSel = (id) => v.public_ids?.includes(id);

    const toggleNode = (id) => {
        if (disabled) return;
        const next = isNodeSel(id) ? v.node_ids.filter((x) => x !== id) : [...v.node_ids, id];
        onChange?.({ ...v, node_ids: next });
    };
    const togglePub = (id) => {
        if (disabled) return;
        const cur = v.public_ids || [];
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        onChange?.({ ...v, public_ids: next });
    };
    const addCustom = () => {
        const ip = customInput.trim();
        if (!ip) return;
        if (!isValidIp(ip)) {
            setCustomError('IP inválido');
            return;
        }
        if (v.custom_servers?.includes(ip)) {
            setCustomError('IP já adicionado');
            return;
        }
        setCustomError('');
        onChange?.({ ...v, custom_servers: [...(v.custom_servers || []), ip] });
        setCustomInput('');
    };
    const removeCustom = (ip) => {
        if (disabled) return;
        onChange?.({ ...v, custom_servers: (v.custom_servers || []).filter((x) => x !== ip) });
    };

    const totalSelected =
        (v.node_ids?.length || 0) +
        (v.public_ids?.length || 0) +
        (v.custom_servers?.length || 0);

    return (
        <Box sx={{
            mb: 2, p: 1.5,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
        }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, fontWeight: 600 }}>
                Servidores DNS — {totalSelected} selecionado{totalSelected !== 1 ? 's' : ''}
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Carregando...</Typography>
                </Box>
            ) : (
                <>
                    {/* Nós configurados */}
                    {nodes.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Nós configurados
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {nodes.map((n) => {
                                    const selected = isNodeSel(n.id);
                                    return (
                                        <Tooltip key={n.id} title={n.ip}>
                                            <Chip
                                                icon={<DnsIcon />}
                                                label={n.name}
                                                clickable
                                                size="small"
                                                disabled={disabled}
                                                color={selected ? 'primary' : 'default'}
                                                variant={selected ? 'filled' : 'outlined'}
                                                onClick={() => toggleNode(n.id)}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* DNS público */}
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            DNS público
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {publics.map((p) => {
                                const selected = isPubSel(p.id);
                                return (
                                    <Tooltip key={p.id} title={p.server}>
                                        <Chip
                                            icon={<PublicIcon />}
                                            label={p.name}
                                            clickable
                                            size="small"
                                            disabled={disabled}
                                            color={selected ? 'success' : 'default'}
                                            variant={selected ? 'filled' : 'outlined'}
                                            onClick={() => togglePub(p.id)}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* DNS custom */}
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            DNS customizado (qualquer IP)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                            {(v.custom_servers || []).map((ip) => (
                                <Chip
                                    key={ip}
                                    icon={<PublicIcon />}
                                    label={ip}
                                    size="small"
                                    color="info"
                                    onDelete={disabled ? undefined : () => removeCustom(ip)}
                                    deleteIcon={<CloseIcon />}
                                />
                            ))}
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="ex.: 200.221.11.100"
                                value={customInput}
                                onChange={(e) => { setCustomInput(e.target.value); setCustomError(''); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                                disabled={disabled}
                                error={!!customError}
                                helperText={customError || ' '}
                                sx={{ flexGrow: 1, maxWidth: 280 }}
                            />
                            <IconButton
                                size="small" onClick={addCustom}
                                disabled={disabled || !customInput.trim()}
                                sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                </>
            )}
        </Box>
    );
}
